import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { FaClock, FaHandPaper } from "react-icons/fa";
import axios from "../../utils/axiosInstance";
import PageWrapper from "../../components/ui/PageWrapper";
import PageHeader from "../../components/ui/PageHeader";
import Button from "../../components/ui/Button";
import FormModal from "../../components/admin/FormModal";
import TaskBuilderModal from "../../components/ProjectTasks/TaskBuilderModal";
import LineItemAssignmentToolbar from "../../components/admin/LineItemAssignmentToolbar";
import InvoiceReviewModal from "../../components/admin/InvoiceReviewModal";
import { InvoiceConfidenceCell, InvoiceStatusBadge } from "../../components/admin/InvoiceTableCells";
import {
  getBillingLabel,
  getVisibleLineItemCount,
  getVisibleLineItemSum,
  hasPriceDiff,
  normalizeProjectId,
} from "../../utils/invoiceDisplayUtils";
import { getTaskLabel } from "../../components/ProjectEconomy/expectedCostUtils";
import { viewInvoicePdf } from "../../utils/invoicePdfUtils";
import { deleteInvoiceWithConfirm } from "../../utils/invoiceDeleteUtils";
import dayjs from "dayjs";

const GMAIL_LINK_BASE = "https://mail.google.com/mail/u/0/#all/";
const SYNC_HISTORY_PAGE_SIZE = 5;
const RESYNC_ALL_PROJECTS = "__all__";

export default function InvoiceInboxPage() {
  const [syncState, setSyncState] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: ["new", "in_review", "reviewed"],
    project: "",
    q: "",
    needsReview: "",
  });
  // Applied params sent to API (only change on Search click, page change, or page size change)
  const [searchParams, setSearchParams] = useState({
    status: ["new", "in_review", "reviewed"],
    project: "",
    q: "",
    needsReview: "",
    page: 1,
    limit: 50,
    sortBy: "invoiceDate",
    sortOrder: "desc",
  });
  const [expandedId, setExpandedId] = useState(null);
  const [reviewInvoice, setReviewInvoice] = useState(null);
  const [projects, setProjects] = useState([]);
  const [taskBundle, setTaskBundle] = useState(null);
  const [resyncFrom, setResyncFrom] = useState("");
  const [resyncProjectId, setResyncProjectId] = useState("");
  const [syncRuns, setSyncRuns] = useState([]);
  const [syncRunsTotal, setSyncRunsTotal] = useState(0);
  const [syncRunsPage, setSyncRunsPage] = useState(1);
  const [syncRunsLoading, setSyncRunsLoading] = useState(false);
  const [cancelingRunId, setCancelingRunId] = useState(null);
  const [syncRunErrorsModal, setSyncRunErrorsModal] = useState(null);
  const [syncRunErrorsData, setSyncRunErrorsData] = useState(null);
  const [syncRunErrorsLoading, setSyncRunErrorsLoading] = useState(false);
  const [openActionsId, setOpenActionsId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const actionsDropdownRef = useRef(null);
  const actionsMenuRef = useRef(null);
  const statusDropdownMenuRef = useRef(null);
  const headerActionsMenuRef = useRef(null);
  const siblingPopoverMenuRef = useRef(null);
  const [statusDropdownPosition, setStatusDropdownPosition] = useState(null);
  const [headerActionsPosition, setHeaderActionsPosition] = useState(null);
  const [siblingPopoverPosition, setSiblingPopoverPosition] = useState(null);
  const ACTIONS_MENU_WIDTH = 160;
  const STATUS_MENU_WIDTH = 160;
  const HEADER_MENU_WIDTH = 176;
  const SIBLING_MENU_WIDTH = 180;
  const VIEWPORT_MARGIN = 8;
  const [headerActionsOpen, setHeaderActionsOpen] = useState(false);
  const headerActionsRef = useRef(null);
  const lastReviewedInvoiceIdRef = useRef(null);
  const [expandedTaskBundle, setExpandedTaskBundle] = useState(null);
  const [expandedTaskBundleLoading, setExpandedTaskBundleLoading] = useState(false);
  const [patchSavingId, setPatchSavingId] = useState(null);
  const [selectedLineItemIdsByInvoice, setSelectedLineItemIdsByInvoice] = useState({});
  const [selectedLineItemIds, setSelectedLineItemIds] = useState([]);
  const [lineItemTaskOverride, setLineItemTaskOverride] = useState("");
  const [lineItemBillingOption, setLineItemBillingOption] = useState("included");
  const [lineItemApplySaving, setLineItemApplySaving] = useState(false);
  const [lineModifyMenuOpen, setLineModifyMenuOpen] = useState(false);
  const lineModifyMenuRef = useRef(null);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [newTaskModalProjectId, setNewTaskModalProjectId] = useState("");
  const [siblingPopoverId, setSiblingPopoverId] = useState(null);
  const siblingPopoverRef = useRef(null);
  const [saveInvoiceLoading, setSaveInvoiceLoading] = useState(false);
  const [deleteInvoiceLoading, setDeleteInvoiceLoading] = useState(false);
  const [saveInvoiceError, setSaveInvoiceError] = useState(null);
  /** Selected invoice IDs for "Mark as Invoiced" (checkbox column) */
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [markInvoicedLoading, setMarkInvoicedLoading] = useState(false);
  const [showMarkInvoicedModal, setShowMarkInvoicedModal] = useState(false);
  const [markInvoicedNumber, setMarkInvoicedNumber] = useState("");
  const [markInvoicedDate, setMarkInvoicedDate] = useState(() => dayjs().format("YYYY-MM-DD"));
  /** Current time for live duration display when a run is in progress */
  const [now, setNow] = useState(() => Date.now());
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);

  const STATUS_OPTIONS = [
    { value: "new", label: "New" },
    { value: "in_review", label: "In review" },
    { value: "reviewed", label: "Reviewed" },
    { value: "invoiced", label: "Invoiced" },
  ];

  const fetchSyncState = useCallback(async () => {
    try {
      const res = await axios.get("/admin/gmail-invoices/sync-state");
      setSyncState(res.data);
    } catch (err) {
      console.error("Sync state", err);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: searchParams.page,
        limit: searchParams.limit,
        sortBy: searchParams.sortBy ?? "invoiceDate",
        sortOrder: searchParams.sortOrder ?? "desc",
      };
      if (Array.isArray(searchParams.status) && searchParams.status.length > 0) {
        params.status = searchParams.status.join(",");
      } else if (searchParams.status && !Array.isArray(searchParams.status)) {
        params.status = searchParams.status;
      }
      if (searchParams.project) params.project = searchParams.project;
      if (searchParams.q) params.q = searchParams.q;
      if (searchParams.needsReview === "true") params.needsReview = "true";
      if (searchParams.needsReview === "false") params.needsReview = "false";
      const res = await axios.get("/admin/invoices", { params });
      setInvoices(res.data.items || []);
      setTotal(res.data.total ?? 0);
    } catch (err) {
      console.error("Invoices", err);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  const fetchSyncRuns = useCallback(async (p = 1, options = {}) => {
    const { silent = false } = options;
    if (!silent) setSyncRunsLoading(true);
    try {
      const res = await axios.get("/admin/gmail-invoices/sync-runs", {
        params: { page: p, limit: SYNC_HISTORY_PAGE_SIZE },
      });
      setSyncRuns(res.data.items || []);
      setSyncRunsTotal(res.data.total ?? 0);
      setSyncRunsPage(res.data.page ?? 1);
    } catch (err) {
      console.error("Sync runs", err);
    } finally {
      if (!silent) setSyncRunsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSyncState();
  }, [fetchSyncState]);

  useEffect(() => {
    fetchSyncRuns(syncRunsPage);
  }, [fetchSyncRuns, syncRunsPage]);

  const hasInProgressRun = syncRuns.some(
    (r) => r.status === "running" || r.status === "processing"
  );

  useEffect(() => {
    if (!hasInProgressRun) return;
    const interval = setInterval(() => {
      fetchSyncRuns(syncRunsPage, { silent: true });
    }, 2500);
    return () => clearInterval(interval);
  }, [hasInProgressRun, syncRunsPage, fetchSyncRuns]);

  useEffect(() => {
    if (!hasInProgressRun) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [hasInProgressRun]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    if (!statusDropdownOpen) return;
    const onMouseDown = (e) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(e.target) &&
        !statusDropdownMenuRef.current?.contains(e.target)
      ) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [statusDropdownOpen]);

  useEffect(() => {
    if (reviewInvoice || !lastReviewedInvoiceIdRef.current) return;
    const row = document.querySelector(
      `[data-invoice-row-id="${lastReviewedInvoiceIdRef.current}"]`
    );
    if (row && typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ behavior: "auto", block: "center" });
    }
  }, [reviewInvoice]);

  useEffect(() => {
    if (reviewInvoice?.projectId) {
      axios
        .get(`/task-bundles/projects/${reviewInvoice.projectId}/task-bundle`)
        .then((res) => setTaskBundle(res.data))
        .catch(() => setTaskBundle(null));
    } else {
      setTaskBundle(null);
    }
  }, [reviewInvoice?.projectId]);

  useEffect(() => {
    if (!expandedId) {
      setExpandedTaskBundle(null);
      setExpandedTaskBundleLoading(false);
      return;
    }
    const inv = invoices.find((i) => i._id === expandedId);
    if (!inv?.projectId) {
      setExpandedTaskBundle(null);
      setExpandedTaskBundleLoading(false);
      return;
    }
    setExpandedTaskBundleLoading(true);
    axios
      .get(`/task-bundles/projects/${inv.projectId}/task-bundle`)
      .then((res) => setExpandedTaskBundle(res.data))
      .catch(() => setExpandedTaskBundle(null))
      .finally(() => setExpandedTaskBundleLoading(false));
  }, [expandedId, invoices]);

  useEffect(() => {
    axios
      .get("/projects", {
        params: {
          limit: 500,
          statuses: "upcoming,in_progress,done",
        },
      })
      .then((res) => {
        setProjects(res.data.items || res.data || []);
      })
      .catch(() => setProjects([]));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;

      if (
        openActionsId &&
        !actionsDropdownRef.current?.contains(target) &&
        !actionsMenuRef.current?.contains(target)
      ) {
        setOpenActionsId(null);
        setDropdownPosition(null);
      }

      if (
        headerActionsOpen &&
        headerActionsRef.current &&
        !headerActionsRef.current.contains(target) &&
        !headerActionsMenuRef.current?.contains(target)
      ) {
        setHeaderActionsOpen(false);
      }

      if (
        siblingPopoverId &&
        !siblingPopoverRef.current?.contains(target) &&
        !siblingPopoverMenuRef.current?.contains(target)
      ) {
        setSiblingPopoverId(null);
        setSiblingPopoverPosition(null);
      }

      if (
        lineModifyMenuOpen &&
        lineModifyMenuRef.current &&
        !lineModifyMenuRef.current.contains(target)
      ) {
        setLineModifyMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openActionsId, headerActionsOpen, siblingPopoverId, lineModifyMenuOpen]);

  const computeFloatingPosition = useCallback(
    ({ triggerRect, menuHeight = 220, menuWidth = ACTIONS_MENU_WIDTH, align = "end" }) => {
    if (!triggerRect) return null;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const opensUpward = spaceBelow < menuHeight + VIEWPORT_MARGIN;

    const unclampedTop = opensUpward
      ? triggerRect.top - menuHeight - 4
      : triggerRect.bottom + 4;
    const unclampedLeft =
      align === "start" ? triggerRect.left : triggerRect.right - menuWidth;

    const top = Math.max(
      VIEWPORT_MARGIN,
      Math.min(unclampedTop, viewportHeight - menuHeight - VIEWPORT_MARGIN)
    );
    const left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(unclampedLeft, viewportWidth - menuWidth - VIEWPORT_MARGIN)
    );

    return { top, left };
    },
    [ACTIONS_MENU_WIDTH]
  );

  useEffect(() => {
    if (!openActionsId) return;

    const updatePosition = () => {
      const trigger = document.querySelector(
        `[data-invoice-actions-trigger="${openActionsId}"]`
      );
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      setDropdownPosition(
        computeFloatingPosition({
          triggerRect: rect,
          menuWidth: ACTIONS_MENU_WIDTH,
          align: "end",
        })
      );
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [openActionsId, computeFloatingPosition]);

  useEffect(() => {
    if (!openActionsId || !actionsMenuRef.current) return;
    const menuHeight = actionsMenuRef.current.getBoundingClientRect().height;
    const trigger = document.querySelector(
      `[data-invoice-actions-trigger="${openActionsId}"]`
    );
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setDropdownPosition(
      computeFloatingPosition({
        triggerRect: rect,
        menuHeight,
        menuWidth: ACTIONS_MENU_WIDTH,
        align: "end",
      })
    );
  }, [openActionsId, dropdownPosition?.top, computeFloatingPosition]);

  useEffect(() => {
    if (!statusDropdownOpen) return;
    const updatePosition = () => {
      const trigger = document.querySelector('[data-status-dropdown-trigger="true"]');
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      setStatusDropdownPosition(
        computeFloatingPosition({
          triggerRect: rect,
          menuWidth: STATUS_MENU_WIDTH,
          align: "start",
        })
      );
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [statusDropdownOpen, computeFloatingPosition]);

  useEffect(() => {
    if (!statusDropdownOpen || !statusDropdownMenuRef.current) return;
    const trigger = document.querySelector('[data-status-dropdown-trigger="true"]');
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuHeight = statusDropdownMenuRef.current.getBoundingClientRect().height;
    setStatusDropdownPosition(
      computeFloatingPosition({
        triggerRect: rect,
        menuHeight,
        menuWidth: STATUS_MENU_WIDTH,
        align: "start",
      })
    );
  }, [statusDropdownOpen, statusDropdownPosition?.top, computeFloatingPosition]);

  useEffect(() => {
    if (!headerActionsOpen) return;
    const updatePosition = () => {
      const trigger = document.querySelector('[data-header-actions-trigger="true"]');
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      setHeaderActionsPosition(
        computeFloatingPosition({
          triggerRect: rect,
          menuWidth: HEADER_MENU_WIDTH,
          align: "end",
        })
      );
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [headerActionsOpen, computeFloatingPosition]);

  useEffect(() => {
    if (!headerActionsOpen || !headerActionsMenuRef.current) return;
    const trigger = document.querySelector('[data-header-actions-trigger="true"]');
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuHeight = headerActionsMenuRef.current.getBoundingClientRect().height;
    setHeaderActionsPosition(
      computeFloatingPosition({
        triggerRect: rect,
        menuHeight,
        menuWidth: HEADER_MENU_WIDTH,
        align: "end",
      })
    );
  }, [headerActionsOpen, headerActionsPosition?.top, computeFloatingPosition]);

  useEffect(() => {
    if (!siblingPopoverId) return;
    const updatePosition = () => {
      const trigger = document.querySelector(
        `[data-sibling-popover-trigger="${siblingPopoverId}"]`
      );
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      setSiblingPopoverPosition(
        computeFloatingPosition({
          triggerRect: rect,
          menuWidth: SIBLING_MENU_WIDTH,
          align: "start",
        })
      );
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [siblingPopoverId, computeFloatingPosition]);

  useEffect(() => {
    if (!siblingPopoverId || !siblingPopoverMenuRef.current) return;
    const trigger = document.querySelector(
      `[data-sibling-popover-trigger="${siblingPopoverId}"]`
    );
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuHeight = siblingPopoverMenuRef.current.getBoundingClientRect().height;
    setSiblingPopoverPosition(
      computeFloatingPosition({
        triggerRect: rect,
        menuHeight,
        menuWidth: SIBLING_MENU_WIDTH,
        align: "start",
      })
    );
  }, [siblingPopoverId, siblingPopoverPosition?.top, computeFloatingPosition]);

  const handleCancelSyncRun = async (runId) => {
    if (
      !window.confirm(
        "Cancel this sync run? Queued jobs will be removed and pending ingests marked failed."
      )
    ) {
      return;
    }
    setCancelingRunId(runId);
    try {
      await axios.post(`/admin/gmail-invoices/sync-runs/${runId}/cancel`);
      await fetchSyncRuns(syncRunsPage, { silent: true });
    } catch (err) {
      console.error("Cancel sync run", err);
      alert(err.response?.data?.message || "Could not cancel sync run");
    } finally {
      setCancelingRunId(null);
    }
  };

  const handleOpenSyncRunErrors = async (run) => {
    setSyncRunErrorsModal(run);
    setSyncRunErrorsData(null);
    setSyncRunErrorsLoading(true);
    try {
      const res = await axios.get(`/admin/gmail-invoices/sync-runs/${run._id}/errors`);
      setSyncRunErrorsData(res.data);
    } catch (err) {
      console.error("Sync run errors", err);
      setSyncRunErrorsData({ error: err.response?.data?.message || "Could not load errors" });
    } finally {
      setSyncRunErrorsLoading(false);
    }
  };

  const handleCloseSyncRunErrors = () => {
    setSyncRunErrorsModal(null);
    setSyncRunErrorsData(null);
  };

  const handleResync = async () => {
    if (!resyncFrom || !resyncProjectId) return;

    const body = { fromDateISO: new Date(resyncFrom).toISOString() };
    if (resyncProjectId !== RESYNC_ALL_PROJECTS) {
      const project = projects.find((p) => p._id === resyncProjectId);
      if (!project?.name) return;
      body.projectCode = project.name;
    }

    setSyncLoading(true);
    try {
      await axios.post("/admin/gmail-invoices/resync", body);
      setResyncFrom("");
      setResyncProjectId("");
      await fetchSyncState();
      setTimeout(() => fetchSyncRuns(1), 1500);
    } catch (err) {
      console.error("Resync", err);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSort = (column) => {
    setSearchParams((prev) => {
      const nextOrder =
        prev.sortBy === column
          ? prev.sortOrder === "desc"
            ? "asc"
            : "desc"
          : "desc";
      return {
        ...prev,
        sortBy: column,
        sortOrder: nextOrder,
        page: 1,
      };
    });
  };

  const SortableHeader = ({ column, label }) => {
    const isActive = searchParams.sortBy === column;
    const order = searchParams.sortOrder;
    return (
      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
        <button
          type="button"
          onClick={() => handleSort(column)}
          className="inline-flex items-center gap-0.5 uppercase hover:text-gray-700 focus:outline-none"
        >
          {label}
          {isActive && (
            <span className="text-gray-400" aria-hidden>
              {order === "desc" ? "↓" : "↑"}
            </span>
          )}
        </button>
      </th>
    );
  };

  const handleMarkReviewed = async (id) => {
    try {
      await axios.post(`/admin/invoices/${id}/mark-reviewed`);
      setReviewInvoice(null);
      setSelectedLineItemIds([]);
      fetchInvoices();
    } catch (err) {
      console.error("Mark reviewed", err);
    }
  };

  function toggleInvoiceSelection(id) {
    setSelectedInvoiceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    if (!selectedInvoiceIds.includes(id)) {
      if (expandedId) {
        setSelectedLineItemIdsByInvoice((prev) => {
          const next = { ...prev };
          delete next[expandedId];
          return next;
        });
      }
      setExpandedId(null);
    }
  }

  function toggleAllInvoices() {
    const ids = invoices.map((inv) => inv._id).filter(Boolean);
    const allSelected = ids.length > 0 && ids.every((id) => selectedInvoiceIds.includes(id));
    setSelectedInvoiceIds(allSelected ? [] : ids);
  }

  const handleMarkInvoiced = async () => {
    if (selectedInvoiceIds.length === 0) return;
    setShowMarkInvoicedModal(true);
  };

  const handleMarkInvoicedSubmit = async () => {
    const number = (markInvoicedNumber || "").trim();
    if (!number) return;
    if (!markInvoicedDate) return;
    setMarkInvoicedLoading(true);
    try {
      await axios.post("/admin/invoices/mark-invoiced", {
        ids: selectedInvoiceIds,
        invoicedNumber: number,
        invoicedAt: new Date(markInvoicedDate).toISOString(),
      });
      setShowMarkInvoicedModal(false);
      setMarkInvoicedNumber("");
      setMarkInvoicedDate(dayjs().format("YYYY-MM-DD"));
      setSelectedInvoiceIds([]);
      fetchInvoices();
    } catch (err) {
      console.error("Mark as invoiced", err);
    } finally {
      setMarkInvoicedLoading(false);
    }
  };

  const handleMarkDeleted = async (invoice) => {
    setDeleteInvoiceLoading(true);
    try {
      const deleted = await deleteInvoiceWithConfirm(invoice);
      if (!deleted) return;
      const id = invoice._id;
      setOpenActionsId(null);
      setDropdownPosition(null);
      setReviewInvoice(null);
      setSelectedLineItemIds([]);
      setExpandedId((prev) => (prev === id ? null : prev));
      fetchInvoices();
    } catch (err) {
      console.error("Mark deleted", err);
      alert(err.response?.data?.message || "Could not delete invoice");
    } finally {
      setDeleteInvoiceLoading(false);
    }
  };

  const handleReprocess = async (id) => {
    try {
      await axios.post(`/admin/invoices/${id}/reprocess`);
      setReviewInvoice(null);
      setSelectedLineItemIds([]);
      fetchInvoices();
    } catch (err) {
      console.error("Reprocess", err);
    }
  };

  const handleSaveInvoice = async () => {
    if (!reviewInvoice?._id) return;
    setSaveInvoiceError(null);
    setSaveInvoiceLoading(true);
    try {
      const invoiceDate = reviewInvoice.invoiceDate
        ? (reviewInvoice.invoiceDate instanceof Date
            ? reviewInvoice.invoiceDate
            : new Date(reviewInvoice.invoiceDate)
          ).toISOString().slice(0, 10)
        : undefined;
      const payload = {
        projectId: reviewInvoice.projectId || undefined,
        projectCode: reviewInvoice.projectCode,
        billingDecision: reviewInvoice.billingDecision || undefined,
        notes: reviewInvoice.notes,
        status: reviewInvoice.status,
        supplierName: reviewInvoice.supplierName ?? undefined,
        invoiceNumber: reviewInvoice.invoiceNumber ?? undefined,
        invoiceDate: invoiceDate || undefined,
        totals: {
          ...(reviewInvoice.totals || {}),
          ...(reviewInvoice.totals?.net !== undefined &&
            !Number.isNaN(Number(reviewInvoice.totals.net)) && {
              net: Number(reviewInvoice.totals.net),
            }),
          ...(reviewInvoice.totals?.gross !== undefined &&
            !Number.isNaN(Number(reviewInvoice.totals.gross)) && {
              gross: Number(reviewInvoice.totals.gross),
            }),
        },
      };
      const lineItems = (reviewInvoice.lineItems || []).map((li) => {
        const id = li._id != null ? String(li._id) : null;
        const isNew = id && id.startsWith("temp-");
        const num = (v) => (v != null && !Number.isNaN(Number(v)) ? Number(v) : undefined);
        const item = {
          articleNumber: li.articleNumber ?? undefined,
          name: li.name ?? "",
          amount: num(li.amount),
          unit: li.unit ?? undefined,
          net: num(li.net),
          gross: num(li.gross),
          billingDecision: li.billingDecision ?? "inherit",
          taskOverride: li.taskOverride ?? undefined,
          projectOverride: li.projectOverride ?? undefined,
          notes: li.notes ?? undefined,
        };
        if (!isNew && id) item._id = li._id;
        return item;
      });
      payload.lineItems = lineItems;

      const res = await axios.patch(`/admin/invoices/${reviewInvoice._id}`, payload);
      const updated = res.data;
      setInvoices((prev) =>
        prev.map((i) => (i._id === reviewInvoice._id ? updated : i))
      );
      setReviewInvoice(null);
      setSelectedLineItemIds([]);
      fetchInvoices();
    } catch (err) {
      console.error("Save invoice", err);
      setSaveInvoiceError(err.response?.data?.message || "Failed to save invoice");
    } finally {
      setSaveInvoiceLoading(false);
    }
  };

  const openGmail = (msgId) => {
    if (msgId) window.open(`${GMAIL_LINK_BASE}${msgId}`, "_blank");
  };

  const viewPdf = async (invoiceId) => {
    try {
      await viewInvoicePdf(invoiceId);
    } catch (err) {
      console.error("View PDF", err);
      alert(err.response?.data?.message || "Could not load PDF");
    }
  };

  const handleDownloadAllPdfs = async () => {
    const visibleWithPdf = (invoices || []).filter(
      (inv) => inv && inv._id && inv.gmailIngestMessageId
    );
    if (!visibleWithPdf.length) {
      alert("No invoices with PDFs on this page.");
      setHeaderActionsOpen(false);
      return;
    }

    try {
      for (const inv of visibleWithPdf) {
        const res = await axios.get(`/admin/invoices/${inv._id}/pdf`, {
          responseType: "blob",
        });
        const blob = res.data;
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const fileNameParts = [];
        if (inv.invoiceNumber) fileNameParts.push(inv.invoiceNumber);
        if (inv.supplierName) fileNameParts.push(inv.supplierName);
        const baseName =
          fileNameParts.join(" - ").trim() || `invoice-${String(inv._id)}`;
        link.href = url;
        link.download = `${baseName}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (err) {
      console.error("Download PDFs", err);
      alert("Failed to download some invoice PDFs.");
    } finally {
      setHeaderActionsOpen(false);
    }
  };

  const handleCreateTaskSuccess = (payload) => {
    const projectId = normalizeProjectId(
      newTaskModalProjectId || reviewInvoice?.projectId
    );

    const applyBundle = (bundle) => {
      if (!bundle) return;
      if (reviewInvoice && normalizeProjectId(reviewInvoice.projectId) === projectId) {
        setTaskBundle(bundle);
      }
      if (expandedId) {
        const expandedInv = invoices.find((i) => String(i._id) === String(expandedId));
        if (expandedInv && normalizeProjectId(expandedInv.projectId) === projectId) {
          setExpandedTaskBundle(bundle);
        }
      }
    };

    if (payload?.bundle) {
      applyBundle(payload.bundle);
    } else if (projectId) {
      axios
        .get(`/task-bundles/projects/${projectId}/task-bundle`)
        .then((res) => applyBundle(res.data))
        .catch(() => {});
    }

    setShowCreateTaskModal(false);
    setNewTaskModalProjectId("");
  };

  function getTaskLabelInbox(taskOverride, bundle) {
    if (!taskOverride) return "—";
    if (taskOverride === "bundle") return "Task set";
    return getTaskLabel(taskOverride, bundle);
  }

  const handlePatchInvoiceField = async (invoiceId, field, value) => {
    setPatchSavingId(invoiceId);
    try {
      await axios.patch(`/admin/invoices/${invoiceId}`, { [field]: value });
      setInvoices((prev) =>
        prev.map((i) => (i._id === invoiceId ? { ...i, [field]: value } : i))
      );
    } catch (err) {
      console.error("Patch invoice", err);
    } finally {
      setPatchSavingId(null);
    }
  };

  function toggleExpandedLineItemSelection(invoiceId, lineItemId) {
    setSelectedLineItemIdsByInvoice((prev) => {
      const list = prev[invoiceId] || [];
      const set = new Set(list);
      if (set.has(lineItemId)) set.delete(lineItemId);
      else set.add(lineItemId);
      const next = { ...prev, [invoiceId]: [...set] };
      if (next[invoiceId].length === 0) delete next[invoiceId];
      return next;
    });
  }

  function toggleReviewLineItemSelection(lineItemId) {
    setSelectedLineItemIds((prev) => {
      const set = new Set(prev);
      if (set.has(lineItemId)) set.delete(lineItemId);
      else set.add(lineItemId);
      return [...set];
    });
  }

  function clearExpandedLineItemSelection(invoiceId) {
    setSelectedLineItemIdsByInvoice((prev) => {
      const next = { ...prev };
      delete next[invoiceId];
      return next;
    });
  }

  function toggleAllExpandedLineItems(invoiceId, lineItems) {
    const items = lineItems || [];
    if (!items.length) return;
    const current = selectedLineItemIdsByInvoice[invoiceId] || [];
    const allIds = items.map((li) => li._id).filter(Boolean);
    const allSelected = allIds.length > 0 && allIds.every((id) => current.includes(id));
    setSelectedLineItemIdsByInvoice((prev) => {
      const next = { ...prev };
      if (allSelected) {
        delete next[invoiceId];
      } else {
        next[invoiceId] = [...allIds];
      }
      return next;
    });
  }

  function toggleAllReviewLineItems(lineItems) {
    const items = lineItems || [];
    if (!items.length) return;
    const allIds = items.map((li) => li._id).filter(Boolean);
    const allSelected = allIds.length > 0 && allIds.every((id) => selectedLineItemIds.includes(id));
    setSelectedLineItemIds(allSelected ? [] : [...allIds]);
  }

  const handleApplyLineItemTaskAndBilling = async (invoiceId, selectedIds) => {
    const existingIds = (selectedIds || []).filter(
      (id) => typeof id === "string" && !id.startsWith("temp-")
    );
    if (!existingIds.length) return;
    setLineItemApplySaving(true);
    try {
      let updatedInvoice = null;
      for (const lineItemId of existingIds) {
        const res = await axios.patch(
          `/admin/invoices/${invoiceId}/line-items/${lineItemId}`,
          {
            taskOverride: lineItemTaskOverride ?? "",
            billingDecision: lineItemBillingOption,
          }
        );
        updatedInvoice = res.data.invoice;
      }
      if (updatedInvoice) {
        setInvoices((prev) =>
          prev.map((i) => (i._id === invoiceId ? updatedInvoice : i))
        );
        if (reviewInvoice?._id === invoiceId) {
          setReviewInvoice(updatedInvoice);
        }
        clearExpandedLineItemSelection(invoiceId);
        setSelectedLineItemIds([]);
        const inv = invoices.find((i) => i._id === invoiceId);
        const lineItemCount = (inv?.lineItems || []).length;
        const appliedToAll = lineItemCount > 0 && selectedIds.length === lineItemCount;
        if (appliedToAll) {
          const idx = invoices.findIndex((i) => i._id === invoiceId);
          const nextInvoice = idx >= 0 && idx < invoices.length - 1 ? invoices[idx + 1] : null;
          setExpandedId(nextInvoice ? nextInvoice._id : null);
        } else if (updatedInvoice.status === "reviewed") {
          setExpandedId((current) => (current === invoiceId ? null : current));
        }
      }
    } catch (err) {
      console.error("Apply line item task/billing", err);
    } finally {
      setLineItemApplySaving(false);
    }
  };

  const tasks = taskBundle?.tasks || [];

  return (
    <PageWrapper>
      <PageHeader title="Invoice Inbox" />

      {/* Sync settings panel */}
      <section className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h2 className="text-sm font-semibold text-gray-700 mb-2">Sync settings</h2>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span>
            Last sync:{" "}
            {syncState?.lastSuccessfulSyncAt
              ? dayjs(syncState.lastSuccessfulSyncAt).format("D MMM YYYY HH:mm")
              : "—"}
          </span>
          {syncState?.lastError && (
            <span className="text-red-600">Error: {syncState.lastError}</span>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-gray-600">Project</label>
            <select
              value={resyncProjectId}
              onChange={(e) => setResyncProjectId(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="">Select project…</option>
              <option value={RESYNC_ALL_PROJECTS}>All projects</option>
              {projects.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.name} {p.address ? `(${p.address})` : ""}
                </option>
              ))}
            </select>
            <label className="text-gray-600">From date</label>
            <input
              type="date"
              value={resyncFrom}
              onChange={(e) => setResyncFrom(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
            <Button
              onClick={handleResync}
              disabled={!resyncFrom || !resyncProjectId || syncLoading}
              size="sm"
            >
              {syncLoading ? "Syncing…" : "Sync"}
            </Button>
          </div>
        </div>
      </section>

      {/* Sync history */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-gray-700 mb-2">Sync history</h2>
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-8" title="Sync type">
                  Type
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Started</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Project</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Processed</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Skipped</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Invoices synced</th>
                <th
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500"
                  title="Estimated OpenAI cost from recorded token usage (not the OpenAI invoice)"
                >
                  AI cost (est.)
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Since</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Duration</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Error</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {syncRunsLoading ? (
                <tr>
                  <td colSpan={12} className="px-3 py-4 text-center text-gray-500">
                    Loading…
                  </td>
                </tr>
              ) : syncRuns.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-3 py-4 text-center text-gray-500">
                    No sync runs yet
                  </td>
                </tr>
              ) : (
                syncRuns.map((run) => {
                  const isInProgress =
                    run.status === "running" || run.status === "processing";
                  const isScheduled = run.trigger === "scheduled";
                  const errorCount = run.errorCount ?? 0;
                  const duration =
                    run.completedAt && run.startedAt
                      ? Math.round(
                          (new Date(run.completedAt) - new Date(run.startedAt)) /
                            1000
                        )
                      : isInProgress && run.startedAt
                        ? Math.round((now - new Date(run.startedAt).getTime()) / 1000)
                        : null;
                  const statusLabel =
                    run.status === "processing"
                      ? "Processing"
                      : run.status === "running"
                        ? "Running"
                        : run.status === "canceled"
                          ? "Canceled"
                          : run.status;
                  return (
                    <tr key={run._id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                        {isScheduled ? (
                          <FaClock className="w-4 h-4" title="Scheduled sync" aria-label="Scheduled sync" />
                        ) : (
                          <FaHandPaper className="w-4 h-4" title="Manual sync" aria-label="Manual sync" />
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {run.startedAt ? dayjs(run.startedAt).format("D MMM YYYY HH:mm") : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded ${
                            run.status === "completed"
                              ? "bg-green-100 text-green-800"
                              : run.status === "failed"
                                ? "bg-red-100 text-red-800"
                                : run.status === "canceled"
                                  ? "bg-amber-100 text-amber-900"
                                  : run.status === "processing"
                                    ? "bg-blue-100 text-blue-800"
                                    : run.status === "running"
                                      ? "bg-gray-100 text-gray-800"
                                      : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {isInProgress && (
                            <span
                              className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"
                              aria-hidden
                            />
                          )}
                          {statusLabel}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {run.projectCode ? (
                          run.projectCode
                        ) : (
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            All
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">{run.processed ?? "—"}</td>
                      <td className="px-3 py-2">{run.skipped ?? "—"}</td>
                      <td className="px-3 py-2">{run.invoicesCreated ?? 0}</td>
                      <td
                        className="px-3 py-2 whitespace-nowrap"
                        title={
                          run.aiCallCount > 0
                            ? `${run.aiCallCount} call${run.aiCallCount === 1 ? "" : "s"} · ${Number(run.aiInputTokens || 0).toLocaleString()} in / ${Number(run.aiOutputTokens || 0).toLocaleString()} out`
                            : "No AI usage recorded for this run"
                        }
                      >
                        {run.aiEstimatedUsd != null && run.aiCallCount > 0
                          ? `~$${Number(run.aiEstimatedUsd).toFixed(
                              Number(run.aiEstimatedUsd) < 0.01 ? 4 : 2
                            )}`
                          : "—"}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {run.since ? dayjs(run.since).format("D MMM YYYY") : "—"}
                      </td>
                      <td className="px-3 py-2">
                        {duration != null ? (
                          <span className="inline-flex items-center gap-1">
                            {isInProgress && (
                              <span
                                className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"
                                aria-hidden
                              />
                            )}
                            {duration}s
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {errorCount > 0 ? (
                          <button
                            type="button"
                            onClick={() => handleOpenSyncRunErrors(run)}
                            className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                          >
                            {errorCount} {errorCount === 1 ? "error" : "errors"}
                          </button>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {isInProgress ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={cancelingRunId === run._id}
                            onClick={() => handleCancelSyncRun(run._id)}
                          >
                            {cancelingRunId === run._id ? "Canceling…" : "Cancel"}
                          </Button>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {syncRunsTotal > SYNC_HISTORY_PAGE_SIZE && (
          <div className="mt-2 flex justify-between items-center text-sm">
            <span>
              Page {syncRunsPage} of {Math.ceil(syncRunsTotal / SYNC_HISTORY_PAGE_SIZE)}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSyncRunsPage((p) => Math.max(1, p - 1))}
                disabled={syncRunsPage <= 1}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSyncRunsPage((p) => p + 1)}
                disabled={syncRunsPage >= Math.ceil(syncRunsTotal / SYNC_HISTORY_PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </section>

      <FormModal
        isOpen={!!syncRunErrorsModal}
        onClose={handleCloseSyncRunErrors}
        title="Sync run errors"
        size="xl"
      >
        {syncRunErrorsLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : syncRunErrorsData?.error ? (
          <p className="text-sm text-red-600">{syncRunErrorsData.error}</p>
        ) : syncRunErrorsData ? (
          <div className="space-y-6 text-sm">
            {syncRunErrorsData.run?.errorMessage && (
              <section>
                <h3 className="font-semibold text-gray-800 mb-2">Run error</h3>
                <p className="text-red-700 bg-red-50 rounded p-3">{syncRunErrorsData.run.errorMessage}</p>
                {(syncRunErrorsData.run.parseFailureCount ?? 0) > 0 && (
                  <p className="text-gray-600 mt-2">
                    Parse failures: {syncRunErrorsData.run.parseFailureCount}
                  </p>
                )}
              </section>
            )}
            {syncRunErrorsData.failedJobs?.length > 0 && (
              <section>
                <h3 className="font-semibold text-gray-800 mb-2">
                  Failed jobs ({syncRunErrorsData.failedJobs.length})
                </h3>
                <ul className="space-y-3 max-h-64 overflow-y-auto">
                  {syncRunErrorsData.failedJobs.map((job) => (
                    <li key={job._id} className="border rounded p-3 bg-gray-50">
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 mb-1">
                        <span>{job.queue}</span>
                        {job.failedAt && (
                          <span>{dayjs(job.failedAt).format("D MMM YYYY HH:mm")}</span>
                        )}
                      </div>
                      <p className="text-red-700">{job.failureReason || "Unknown error"}</p>
                      {job.payload?.gmailMessageId && (
                        <p className="text-gray-600 mt-1 text-xs">
                          Message: {job.payload.gmailMessageId}
                          {job.payload.projectCode ? ` · ${job.payload.projectCode}` : ""}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {syncRunErrorsData.failedIngests?.length > 0 && (
              <section>
                <h3 className="font-semibold text-gray-800 mb-2">
                  Failed ingests ({syncRunErrorsData.failedIngests.length})
                </h3>
                <ul className="space-y-2 max-h-48 overflow-y-auto">
                  {syncRunErrorsData.failedIngests.map((ingest) => (
                    <li key={ingest._id} className="border rounded p-2 bg-gray-50 text-xs">
                      <span className="font-medium">{ingest.projectCode || "—"}</span>
                      {ingest.subject && <span className="text-gray-600"> · {ingest.subject}</span>}
                      {ingest.from && <span className="text-gray-500 block">{ingest.from}</span>}
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {!syncRunErrorsData.run?.errorMessage &&
              !syncRunErrorsData.failedJobs?.length &&
              !syncRunErrorsData.failedIngests?.length && (
                <p className="text-gray-500">No error details available.</p>
              )}
          </div>
        ) : null}
      </FormModal>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative inline-block" ref={(el) => { statusDropdownRef.current = el; }}>
          <button
            type="button"
            data-status-dropdown-trigger="true"
            onClick={() => setStatusDropdownOpen((o) => !o)}
            className="border rounded px-2 py-1 text-sm bg-white min-w-[140px] text-left flex items-center justify-between gap-2"
          >
            <span>
              Status {(Array.isArray(filters.status) ? filters.status.length : 0) === 4 ? "All" : (filters.status?.length || 0) === 0 ? "All" : `${filters.status?.length || 0} selected`}
            </span>
            <span className="text-gray-400">{statusDropdownOpen ? "▲" : "▼"}</span>
          </button>
          {statusDropdownOpen &&
            statusDropdownPosition &&
            createPortal(
              <div
                ref={statusDropdownMenuRef}
                className="fixed z-50 min-w-[160px] py-2 px-2 bg-white border border-gray-200 rounded shadow-lg"
                style={{
                  top: statusDropdownPosition.top,
                  left: statusDropdownPosition.left,
                }}
              >
              {STATUS_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 py-1 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={(filters.status || []).includes(opt.value)}
                    onChange={() => {
                      setFilters((f) => ({
                        ...f,
                        status: (f.status || []).includes(opt.value)
                          ? (f.status || []).filter((s) => s !== opt.value)
                          : [...(f.status || []), opt.value],
                      }));
                    }}
                    className="rounded border-gray-300"
                  />
                  {opt.label}
                </label>
              ))}
              <div className="border-t mt-1 pt-1 flex gap-1">
                <button
                  type="button"
                  onClick={() => setFilters((f) => ({ ...f, status: STATUS_OPTIONS.map((o) => o.value) }))}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => setFilters((f) => ({ ...f, status: [] }))}
                  className="text-xs text-gray-600 hover:text-gray-800"
                >
                  Clear
                </button>
              </div>
              </div>,
              document.body
            )}
        </div>
        <input
          type="text"
          placeholder="Project / search"
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          className="border rounded px-2 py-1 text-sm w-40"
        />
        <select
          value={filters.needsReview}
          onChange={(e) => setFilters((f) => ({ ...f, needsReview: e.target.value }))}
          className="border rounded px-2 py-1 text-sm"
        >
          <option value="">All</option>
          <option value="true">Needs review</option>
          <option value="false">OK</option>
        </select>
        <Button
          size="sm"
          onClick={() =>
            setSearchParams((prev) => ({
              ...prev,
              ...filters,
              page: 1,
            }))
          }
        >
          Search
        </Button>
        <span className="text-gray-500 text-sm">Page size</span>
        <select
          value={searchParams.limit === 9999 ? "all" : searchParams.limit}
          onChange={(e) => {
            const v = e.target.value;
            const limit = v === "all" ? 9999 : parseInt(v, 10);
            setSearchParams((prev) => ({ ...prev, limit, page: 1 }));
          }}
          className="border rounded px-2 py-1 text-sm"
        >
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value="all">All</option>
        </select>
        <span className="text-sm text-gray-600 ml-2">
          {total} result{total !== 1 ? "s" : ""}
        </span>
        {selectedInvoiceIds.length > 0 && (
          <Button
            size="sm"
            onClick={handleMarkInvoiced}
            disabled={markInvoicedLoading}
            className="ml-2"
          >
            {markInvoicedLoading ? "Updating…" : `Mark as Invoiced (${selectedInvoiceIds.length})`}
          </Button>
        )}
      </div>

      {/* Mark as Invoiced modal */}
      {showMarkInvoicedModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => !markInvoicedLoading && setShowMarkInvoicedModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="mark-invoiced-title"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="mark-invoiced-title" className="text-lg font-semibold mb-2">
              Mark as Invoiced
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {selectedInvoiceIds.length} invoice{selectedInvoiceIds.length !== 1 ? "s" : ""} selected. Enter our invoice number and date for reporting.
            </p>
            <div className="space-y-3">
              <div>
                <label htmlFor="mark-invoiced-number" className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice number
                </label>
                <input
                  id="mark-invoiced-number"
                  type="text"
                  value={markInvoicedNumber}
                  onChange={(e) => setMarkInvoicedNumber(e.target.value)}
                  placeholder="e.g. INV-2024-001"
                  className="border rounded px-2 py-1.5 w-full"
                  autoFocus
                />
              </div>
              <div>
                <label htmlFor="mark-invoiced-date" className="block text-sm font-medium text-gray-700 mb-1">
                  Date of invoice
                </label>
                <input
                  id="mark-invoiced-date"
                  type="date"
                  value={markInvoicedDate}
                  onChange={(e) => setMarkInvoicedDate(e.target.value)}
                  className="border rounded px-2 py-1.5 w-full"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => !markInvoicedLoading && setShowMarkInvoicedModal(false)}
                disabled={markInvoicedLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleMarkInvoicedSubmit}
                disabled={markInvoicedLoading || !markInvoicedNumber.trim() || !markInvoicedDate}
              >
                {markInvoicedLoading ? "Updating…" : "Mark as Invoiced"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-8">
                {!Object.values(selectedLineItemIdsByInvoice).some((arr) => arr && arr.length > 0) ? (
                  <input
                    type="checkbox"
                    checked={invoices.length > 0 && invoices.every((inv) => selectedInvoiceIds.includes(inv._id))}
                    onChange={toggleAllInvoices}
                    className="rounded border-gray-300"
                    title="Select all on page"
                  />
                ) : null}
              </th>
              <SortableHeader column="emailDate" label="Email date" />
              <SortableHeader column="invoiceDate" label="Invoice date" />
              <SortableHeader column="supplierName" label="Supplier" />
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Invoice #</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Net</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Gross</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Project</th>
              <SortableHeader column="status" label="Status" />
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Conf.</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 w-20">
                <div
                  className="relative inline-flex items-center gap-1"
                  ref={headerActionsRef}
                >
                  <span>Actions</span>
                  <button
                    type="button"
                    data-header-actions-trigger="true"
                    onClick={(e) => {
                      e.stopPropagation();
                      setHeaderActionsOpen((open) => !open);
                    }}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    aria-label="Invoice list actions"
                    aria-haspopup="menu"
                    aria-expanded={headerActionsOpen}
                  >
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                      />
                    </svg>
                  </button>
                  {headerActionsOpen &&
                    headerActionsPosition &&
                    createPortal(
                      <div
                        ref={headerActionsMenuRef}
                        className="fixed w-44 bg-white rounded-md shadow-lg border border-gray-200 z-50 py-1"
                        style={{
                          top: headerActionsPosition.top,
                          left: headerActionsPosition.left,
                        }}
                      >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadAllPdfs();
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Download PDFs
                      </button>
                      </div>,
                      document.body
                    )}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={11} className="px-3 py-4 text-center text-gray-500">Loading…</td></tr>
            ) : invoices.length === 0 ? (
              <tr><td colSpan={11} className="px-3 py-4 text-center text-gray-500">No invoices</td></tr>
            ) : (
              invoices.map((inv) => (
                <React.Fragment key={inv._id}>
                  <tr
                    data-invoice-row-id={inv._id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      const nextExpandedId = expandedId === inv._id ? null : inv._id;
                      if (expandedId) {
                        setSelectedLineItemIdsByInvoice((prev) => {
                          const next = { ...prev };
                          delete next[expandedId];
                          return next;
                        });
                      }
                      setExpandedId(nextExpandedId);
                    }}
                  >
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      {!Object.values(selectedLineItemIdsByInvoice).some((arr) => arr && arr.length > 0) ? (
                        <input
                          type="checkbox"
                          checked={selectedInvoiceIds.includes(inv._id)}
                          onChange={() => toggleInvoiceSelection(inv._id)}
                          className="rounded border-gray-300"
                        />
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                      {inv.source?.internalDate ? dayjs(inv.source.internalDate).format("D MMM YYYY") : "—"}
                    </td>
                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                      {inv.invoiceDate ? dayjs(inv.invoiceDate).format("D MMM YYYY") : "—"}
                    </td>
                    <td
                      className="px-3 py-2 text-sm"
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        const text = inv.supplierName || "—";
                        navigator.clipboard.writeText(text).catch(() => {});
                      }}
                      title="Double-click to copy supplier"
                    >
                      <span>{inv.supplierName || "—"}</span>
                      <span className="ml-1 inline-block text-gray-500 align-middle">
                        {expandedId === inv._id ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        )}
                      </span>
                    </td>
                    <td
                      className="px-3 py-2 text-sm"
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        const text = inv.invoiceNumber || "—";
                        navigator.clipboard.writeText(text).catch(() => {});
                      }}
                      title="Double-click to copy invoice number"
                    >
                      {inv.invoiceNumber || "—"}
                    </td>
                    <td
                      className="px-3 py-2 text-sm text-right"
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        const net = inv.totals?.net;
                        const text =
                          net != null && !Number.isNaN(Number(net))
                            ? (Number(net) * 1.15).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            : "—";
                        navigator.clipboard.writeText(text).catch(() => {});
                        axios
                          .patch(`/admin/invoices/${inv._id}`, { status: "invoiced" })
                          .then(() => {
                            setInvoices((prev) =>
                              prev.map((i) => (i._id === inv._id ? { ...i, status: "invoiced" } : i))
                            );
                          })
                          .catch(() => {});
                      }}
                      title="Double-click to copy net + 15% and mark as Invoiced"
                    >
                      {inv.totals?.net != null ? Number(inv.totals.net).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                    </td>
                    <td className="px-3 py-2 text-sm text-right">
                      {inv.totals?.gross != null ? Number(inv.totals.gross).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      {inv.siblingProjectCodes?.length > 1 ? (
                        <div className="relative inline-block" ref={siblingPopoverId === inv._id ? siblingPopoverRef : null}>
                          <span className="inline-block border border-gray-300 rounded px-1.5 py-0.5 bg-transparent">{inv.projectCode || "—"}</span>
                          <button
                            type="button"
                            data-sibling-popover-trigger={inv._id}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (siblingPopoverId === inv._id) {
                                setSiblingPopoverId(null);
                                setSiblingPopoverPosition(null);
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setSiblingPopoverPosition(
                                  computeFloatingPosition({
                                    triggerRect: rect,
                                    menuWidth: SIBLING_MENU_WIDTH,
                                    align: "start",
                                  })
                                );
                                setSiblingPopoverId(inv._id);
                              }
                            }}
                            className="ml-0.5 text-gray-600 hover:text-gray-900 font-medium"
                            aria-label="Show all project tags"
                          >
                            ++
                          </button>
                          {siblingPopoverId === inv._id &&
                            siblingPopoverPosition &&
                            createPortal(
                              <div
                                ref={siblingPopoverMenuRef}
                                className="fixed z-50 min-w-[120px] py-2 px-3 bg-white border border-gray-200 rounded shadow-lg text-left"
                                style={{
                                  top: siblingPopoverPosition.top,
                                  left: siblingPopoverPosition.left,
                                }}
                                role="dialog"
                                aria-label="Project tags"
                              >
                                <div className="text-xs font-medium text-gray-500 mb-1">Project tags</div>
                                <ul className="text-sm">
                                  {(inv.siblingProjectCodes || []).map((code) => (
                                    <li key={code}>{code}</li>
                                  ))}
                                </ul>
                              </div>,
                              document.body
                            )}
                        </div>
                      ) : (
                        <span className="inline-block border border-gray-300 rounded px-1.5 py-0.5 bg-transparent">{inv.projectCode || "—"}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <InvoiceStatusBadge status={inv.status} />
                    </td>
                    <td className="px-3 py-2">
                      <InvoiceConfidenceCell
                        invoice={inv}
                        onOpenReview={(invoice) => {
                          lastReviewedInvoiceIdRef.current = invoice._id;
                          setReviewInvoice(invoice);
                          setSaveInvoiceError(null);
                        }}
                      />
                    </td>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <div
                        className="relative inline-block"
                        ref={openActionsId === inv._id ? actionsDropdownRef : null}
                      >
                        <button
                          type="button"
                          data-invoice-actions-trigger={inv._id}
                          onClick={(e) => {
                            if (openActionsId === inv._id) {
                              setOpenActionsId(null);
                              setDropdownPosition(null);
                            } else {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setDropdownPosition(
                                computeFloatingPosition({
                                  triggerRect: rect,
                                  menuWidth: ACTIONS_MENU_WIDTH,
                                  align: "end",
                                })
                              );
                              setOpenActionsId(inv._id);
                            }
                          }}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          aria-label="Invoice actions"
                          aria-expanded={openActionsId === inv._id}
                        >
                          <svg
                            className="w-5 h-5 text-gray-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === inv._id && (
                    <tr>
                      <td className="w-8 px-3 py-2 bg-gray-50" aria-hidden />
                      <td colSpan={10} className="px-3 py-2 bg-gray-50">
                        <div className="text-sm">
                          <div className="flex items-baseline justify-between gap-4 mb-1">
                            <span className="font-medium">Line items</span>
                            <span className="text-xs">
                              <span style={{ fontWeight: "bold" }}>Buyer(s):</span>{" "}
                              <span className="font-medium text-gray-500">
                                {(inv.ourReferences || []).length > 0
                                  ? (inv.ourReferences || []).map((ref) => ref.name ?? "—").join(", ")
                                  : "—"}
                              </span>
                            </span>
                          </div>
                          <LineItemAssignmentToolbar
                            selectedCount={(selectedLineItemIdsByInvoice[inv._id] || []).length}
                            taskBundle={expandedTaskBundle}
                            taskOverride={lineItemTaskOverride}
                            onTaskOverrideChange={setLineItemTaskOverride}
                            billingOption={lineItemBillingOption}
                            onBillingOptionChange={setLineItemBillingOption}
                            onApply={() =>
                              handleApplyLineItemTaskAndBilling(
                                inv._id,
                                selectedLineItemIdsByInvoice[inv._id] || []
                              )
                            }
                            onClearSelection={() => clearExpandedLineItemSelection(inv._id)}
                            onNewTask={() => {
                              setNewTaskModalProjectId(normalizeProjectId(inv.projectId) || "");
                              setShowCreateTaskModal(true);
                            }}
                            applySaving={lineItemApplySaving}
                            projectId={inv.projectId}
                            size="xs"
                          />
                          <table className="min-w-full text-xs">
                            <thead>
                              <tr>
                                <th className="text-left py-1 w-8">
                                  <input
                                    type="checkbox"
                                    checked={(inv.lineItems || []).length > 0 && ((selectedLineItemIdsByInvoice[inv._id] || []).length === (inv.lineItems || []).length)}
                                    onChange={() => toggleAllExpandedLineItems(inv._id, inv.lineItems)}
                                    className="rounded border-gray-300"
                                    title="Select all line items"
                                  />
                                </th>
                                <th className="text-left py-1">Article #</th>
                                <th className="text-left py-1">Name</th>
                                <th className="text-left py-1">Billing</th>
                                <th className="text-left py-1">Task</th>
                                <th className="text-right py-1 pr-4">Amount</th>
                                <th className="text-left py-1">Unit</th>
                                <th className="text-right py-1">Net</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(inv.lineItems || []).map((li, idx) => {
                                const selectedExpanded = (selectedLineItemIdsByInvoice[inv._id] || []).includes(li._id);
                                const isHidden = li.billingDecision === "hide_from_project";
                                return (
                                  <tr
                                    key={li._id || idx}
                                    className={`${selectedExpanded ? "bg-blue-50" : ""} ${isHidden ? "opacity-70" : ""}`}
                                  >
                                    <td className="py-1">
                                      <input
                                        type="checkbox"
                                        checked={selectedExpanded}
                                        onChange={() => toggleExpandedLineItemSelection(inv._id, li._id)}
                                        className="rounded border-gray-300"
                                      />
                                    </td>
                                    <td className={`py-1 ${isHidden ? "line-through text-gray-500" : ""}`}>{li.articleNumber ?? "—"}</td>
                                    <td className={`py-1 ${isHidden ? "line-through text-gray-500" : ""}`}>
                                      <button
                                        type="button"
                                        onClick={() => toggleExpandedLineItemSelection(inv._id, li._id)}
                                        className="w-full text-left"
                                      >
                                        {li.name ?? "—"}
                                      </button>
                                    </td>
                                    <td className={`py-1 ${isHidden ? "line-through text-gray-500" : ""}`}>{getBillingLabel(li.billingDecision)}</td>
                                    <td className={`py-1 ${isHidden ? "line-through text-gray-500" : ""}`}>{getTaskLabelInbox(li.taskOverride, expandedTaskBundle)}</td>
                                    <td className={`text-right pr-4 ${isHidden ? "line-through text-gray-500" : ""}`}>{li.amount != null ? li.amount : "—"}</td>
                                    <td className={`py-1 ${isHidden ? "line-through text-gray-500" : ""}`}>{li.unit ?? "—"}</td>
                                    <td className={`text-right ${isHidden ? "line-through text-gray-500" : ""}`}>
                                      {li.net != null ? Number(li.net).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot>
                              <tr className="border-t border-gray-200 font-medium">
                                <td className="py-1" colSpan={7}>
                                  {getVisibleLineItemCount(inv.lineItems)} line item{getVisibleLineItemCount(inv.lineItems) !== 1 ? "s" : ""}
                                  {getVisibleLineItemCount(inv.lineItems) !== (inv.lineItems || []).length && (
                                    <span className="text-gray-500 ml-1">
                                      ({(inv.lineItems || []).length - getVisibleLineItemCount(inv.lineItems)} hidden)
                                    </span>
                                  )}
                                </td>
                                <td className="text-right py-1">
                                  {getVisibleLineItemSum(inv.lineItems).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Actions dropdown portal - renders above overflow so it's always visible */}
      {openActionsId && dropdownPosition && (() => {
        const inv = invoices.find((i) => i._id === openActionsId);
        if (!inv) return null;
        return createPortal(
          <div
            ref={actionsMenuRef}
            className="fixed w-40 bg-white rounded-md shadow-lg border border-gray-200 z-50 py-1"
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          >
            <button
              type="button"
              onClick={() => {
                setOpenActionsId(null);
                setDropdownPosition(null);
                lastReviewedInvoiceIdRef.current = inv._id;
                setReviewInvoice(inv);
                setSaveInvoiceError(null);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Review
            </button>
            {inv.gmailIngestMessageId && (
              <button
                type="button"
                onClick={() => {
                  setOpenActionsId(null);
                  setDropdownPosition(null);
                  viewPdf(inv._id);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                View PDF
              </button>
            )}
            {inv.source?.gmailMessageId && (
              <button
                type="button"
                onClick={() => {
                  setOpenActionsId(null);
                  setDropdownPosition(null);
                  openGmail(inv.source.gmailMessageId);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Open in Gmail
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setOpenActionsId(null);
                setDropdownPosition(null);
                handleReprocess(inv._id);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Reprocess
            </button>
            <button
              type="button"
              onClick={() => {
                setOpenActionsId(null);
                setDropdownPosition(null);
                handleMarkReviewed(inv._id);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Mark as Reviewed
            </button>
            <button
              type="button"
              onClick={() => {
                setOpenActionsId(null);
                setDropdownPosition(null);
                handleMarkDeleted(inv);
              }}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              Delete
            </button>
          </div>,
          document.body
        );
      })()}

      {/* Pagination */}
      {total > (searchParams.limit === 9999 ? total : searchParams.limit) && (
        <div className="mt-2 flex justify-between items-center text-sm">
          <span>
            Page {searchParams.page} of{" "}
            {searchParams.limit === 9999
              ? 1
              : Math.ceil(total / searchParams.limit)}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setSearchParams((p) => ({
                  ...p,
                  page: Math.max(1, p.page - 1),
                }))
              }
              disabled={searchParams.page <= 1}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setSearchParams((p) => ({ ...p, page: p.page + 1 }))
              }
              disabled={
                searchParams.limit === 9999 ||
                searchParams.page >= Math.ceil(total / searchParams.limit)
              }
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <InvoiceReviewModal
        invoice={reviewInvoice}
        onInvoiceChange={setReviewInvoice}
        onClose={() => {
          setReviewInvoice(null);
          setSelectedLineItemIds([]);
          setSaveInvoiceError(null);
        }}
        onSave={handleSaveInvoice}
        saveLoading={saveInvoiceLoading}
        saveError={saveInvoiceError}
        projects={projects}
        taskBundle={taskBundle}
        selectedLineItemIds={selectedLineItemIds}
        onToggleLineItemSelection={(lineItemId) => {
          setSelectedLineItemIds((prev) => {
            const set = new Set(prev);
            if (set.has(lineItemId)) set.delete(lineItemId);
            else set.add(lineItemId);
            return [...set];
          });
        }}
        onToggleAllLineItems={(items) => toggleAllReviewLineItems(items)}
        onClearLineItemSelection={() => setSelectedLineItemIds([])}
        onDeselectLineItem={(lineItemId) => {
          setSelectedLineItemIds((prev) => prev.filter((id) => id !== lineItemId));
        }}
        lineItemTaskOverride={lineItemTaskOverride}
        onLineItemTaskOverrideChange={setLineItemTaskOverride}
        lineItemBillingOption={lineItemBillingOption}
        onLineItemBillingOptionChange={setLineItemBillingOption}
        onApplyLineItemTaskAndBilling={handleApplyLineItemTaskAndBilling}
        lineItemApplySaving={lineItemApplySaving}
        onNewTask={() => {
          setNewTaskModalProjectId(normalizeProjectId(reviewInvoice?.projectId) || "");
          setShowCreateTaskModal(true);
        }}
        onViewPdf={viewPdf}
        onOpenGmail={openGmail}
        onReprocess={handleReprocess}
        onDelete={handleMarkDeleted}
        deleteLoading={deleteInvoiceLoading}
        showGmailActions
        showReprocess
      />

      {showCreateTaskModal && (
        <TaskBuilderModal
          bundleId={taskBundle?._id}
          projectId={
            newTaskModalProjectId ||
            normalizeProjectId(reviewInvoice?.projectId) ||
            ""
          }
          isOpen={showCreateTaskModal}
          onClose={() => {
            setShowCreateTaskModal(false);
            setNewTaskModalProjectId("");
          }}
          onSuccess={handleCreateTaskSuccess}
          projects={projects}
          initialProjectId={(newTaskModalProjectId || reviewInvoice?.projectId) ?? ""}
          initialTaskType="task"
        />
      )}
    </PageWrapper>
  );
}
